'use client';

import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Scale } from 'lucide-react';
import LoginCard from './login/LoginCard';
import LoginFooter from './login/LoginFooter';
import LoginSuccessOverlay from './login/LoginSuccessOverlay';
import type { Sesion } from '@/lib/auth';

// Re-exportado para no romper los `import type { Sesion } from './Login'`
// ya existentes en AppShell/Sidebar/Topbar/Dashboard/page.tsx.
export type { Sesion };

// Tiempo que se muestra el aviso de acceso concedido antes de pasar al shell.
const RETARDO_TRANSICION_MS = 1100;

// Pantalla de ingreso con el diseño de referencia `public/assets/img/Login.png`
// adaptado al verde institucional: fondo oscuro con haz diagonal de luz, UNA
// tarjeta central plateada con brillo, dividida en dos — izquierda: escudo +
// nombre del sistema + bienvenida; derecha: "Iniciar Sesión" (o el flujo de
// recuperar contraseña). La lógica de ingreso no cambia (useLogin + overlay).
export default function Login({ onLogin }: { onLogin: (s: Sesion) => void }) {
  const [sesionPendiente, setSesionPendiente] = useState<Sesion | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function manejarLoginExitoso(sesion: Sesion) {
    setSesionPendiente(sesion);
    timerRef.current = setTimeout(() => onLogin(sesion), RETARDO_TRANSICION_MS);
  }

  return (
    <div className="relative flex min-h-dvh flex-col overflow-hidden bg-[#04170d]">
      {/* Fondo del diseño de referencia: haz de luz diagonal arriba-izquierda y
          profundidad de color (verde institucional, no azul) abajo-derecha. */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-40 -top-64 h-[36rem] w-[70rem] -rotate-12 bg-gradient-to-br from-white/15 via-white/5 to-transparent blur-3xl" />
        <div className="absolute -bottom-72 -right-40 h-[40rem] w-[60rem] rounded-full bg-gradient-to-tl from-primary/45 via-emerald-950/40 to-transparent blur-3xl" />
      </div>

      <LoginSuccessOverlay nombre={sesionPendiente?.nombre ?? null} />

      <main className="relative z-10 flex flex-1 items-center justify-center p-4 sm:p-8">
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55, delay: 0.15, ease: 'easeOut' }}
          className="grid w-full max-w-5xl overflow-hidden rounded-3xl bg-gradient-to-br from-white via-slate-50 to-slate-200 shadow-[0_0_90px_-20px_rgba(74,222,128,0.35),0_40px_80px_-40px_rgba(0,0,0,0.8)] lg:grid-cols-2"
        >
          {/* ── Izquierda: identidad institucional ─────────────────────── */}
          <div className="flex flex-col items-center justify-center gap-4 border-b border-slate-200 px-8 py-10 text-center lg:border-b-0 lg:border-r lg:px-12">
            <motion.img
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.35, ease: 'easeOut' }}
              src="/assets/img/Escudo_de_Risaralda.svg"
              alt="Escudo del Departamento de Risaralda"
              className="h-36 w-auto object-contain drop-shadow-md sm:h-44"
            />
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
              Departamento de Risaralda
            </p>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Módulo Disciplinario
            </h1>
            <p className="max-w-xs text-sm leading-relaxed text-slate-600">
              Bienvenido al Sistema de Gestión Disciplinaria.
            </p>
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/5 px-4 py-1.5 text-xs font-semibold text-primary">
              <Scale className="size-3.5" />
              Ley 1952 de 2019 y Ley 2094 de 2021
            </span>
          </div>

          {/* ── Derecha: iniciar sesión / recuperar contraseña ─────────── */}
          <div className="flex items-center px-6 py-8 sm:px-10 sm:py-10">
            <LoginCard onLogin={manejarLoginExitoso} />
          </div>
        </motion.div>
      </main>

      <LoginFooter />
    </div>
  );
}
